// src/pages/OtherUserProfile.jsx
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import ProfileHeader from '../components/profile/ProfileHeader';
import UserInfo from '../components/profile/UserInfo';
import ProfessionalProfile from '../components/profile/ProfessionalProfile';
import ActivitySection from '../components/profile/ActivitySection';

// Badge icons defined inline - Updated with proper icons
const BADGE_ICONS = {
  first_steps: (
    <svg className="w-6 h-6 text-green-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 3H21V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 14L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M21 14V21H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="9" cy="9" r="3" stroke="currentColor" strokeWidth="2"/>
    </svg>
  ),
  blogger: (
    <svg className="w-6 h-6 text-green-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
      <path d="M9 9H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M9 13H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M9 17H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  interpreter: (
    <svg className="w-6 h-6 text-green-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="3" y="4" width="18" height="16" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
      <path d="M7 8H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  explorer: (
    <svg className="w-6 h-6 text-green-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
      <path d="M19.4 15A7 7 0 1 0 15 19.4L21 21L19.4 15Z" stroke="currentColor" strokeWidth="2"/>
    </svg>
  ),
  analyst: (
    <svg className="w-6 h-6 text-green-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" fill="currentColor"/>
    </svg>
  ),
  contributor: (
    <svg className="w-6 h-6 text-green-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
      <path d="M9 12H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M12 9V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  streak: (
    <svg className="w-6 h-6 text-green-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.5 14.5C9 15.5 10.5 17 12.5 17S16 15.5 16.5 14.5" stroke="currentColor" strokeWidth="2"/>
      <path d="M12 2C13 8 16 10 22 12C16 14 13 16 12 22C11 16 8 14 2 12C8 10 11 8 12 2Z" stroke="currentColor" strokeWidth="2"/>
    </svg>
  ),
  verified: (
    <svg className="w-6 h-6 text-green-400" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  ),
  specialist: (
    <svg className="w-6 h-6 text-green-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="8" r="5" stroke="currentColor" strokeWidth="2"/>
      <path d="M8.21 13.89L7 23L12 20L17 23L15.79 13.88" stroke="currentColor" strokeWidth="2"/>
    </svg>
  ),
  community: (
    <svg className="w-6 h-6 text-green-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2"/>
      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
      <path d="M23 21V19C23 18.1645 22.7155 17.3541 22.2094 16.691C21.7033 16.028 20.9999 15.5453 20.2 15.3163" stroke="currentColor" strokeWidth="2"/>
      <path d="M16 3.13C16.8003 3.35891 17.5037 3.84163 18.0098 4.5046C18.5159 5.1676 18.8004 5.9781 18.8004 6.815C18.8004 7.6519 18.5159 8.4624 18.0098 9.1254C17.5037 9.7884 16.8003 10.2711 16 10.5" stroke="currentColor" strokeWidth="2"/>
    </svg>
  ),
};

// Convert backend profile data to frontend format
const transformProfileData = (backendProfile, user) => {
  const professionalInfo = backendProfile.info?.professional || {};

  // Helper function to construct full image URLs
  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath; // Already a full URL
    return `https://app.writeapp.in${imagePath}`;
  };

  return {
    id: backendProfile.user_id,
    name: backendProfile.display_name,
    title: backendProfile.info?.title || '',
    location: backendProfile.city || '',
    profilePicture: getFullImageUrl(backendProfile.profile_pic),
    isVerified: backendProfile.badges?.verified || false,
    streak: backendProfile.info?.streak || 0,
    level: backendProfile.level,
    xp: backendProfile.exp,
    totalXp: backendProfile.exp,
    isCommunityChampion: backendProfile.badges?.community_champion || false,
    badges: backendProfile.badges || {},
    stats: {
      followers: backendProfile.info?.stats?.followers || 0,
      following: backendProfile.info?.stats?.following || 0,
      posts: backendProfile.info?.stats?.posts || 0,
      interpretations: backendProfile.info?.stats?.interpretations || 0,
      comments: backendProfile.info?.stats?.comments || 0,
      blogs: backendProfile.info?.stats?.blogs || 0,
      upvotes: backendProfile.info?.stats?.upvotes || 0,
      highestUpvotedPost: backendProfile.info?.stats?.highest_upvoted_post || 0,
      highestSharedPost: backendProfile.info?.stats?.highest_shared_post || 0
    },
    professional: {
      summary: backendProfile.bio || '',
      services: professionalInfo.services || [],
      certifications: professionalInfo.certifications || [],
      education: professionalInfo.education || [],
      website: professionalInfo.website || '',
      email: professionalInfo.email || '',
      available: professionalInfo.available || false
    },
    activity: {
      posts: (backendProfile.info?.activity?.posts || []).map(post => ({
        ...post,
        image_url: getFullImageUrl(post.image_url)
      })),
      comments: backendProfile.info?.activity?.comments || [],
      interpretations: backendProfile.info?.activity?.interpretations || [],
      blogs: (backendProfile.info?.activity?.blogs || []).map(blog => ({
        ...blog,
        cover_image: getFullImageUrl(blog.cover_image)
      }))
    }
  };
};

// Badges Section Component - Updated to match BadgesSection design
const OtherUserBadgesSection = ({ userData, showAllBadges, setShowAllBadges }) => {
  const [showTooltip, setShowTooltip] = useState({});

  // Get only achieved badges from the badges object
  const achievedBadges = useMemo(() => {
    if (!userData?.badges || typeof userData.badges !== 'object') return [];
    
    return Object.entries(userData.badges)
      .filter(([_, badgeData]) => badgeData && badgeData.earned_at)
      .map(([badgeKey, badgeData]) => ({
        id: badgeKey,
        ...badgeData,
        icon: BADGE_ICONS[badgeKey] || (
          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
            <span className="text-sm text-white font-bold">?</span>
          </div>
        ),
        unlocked: true,
        // All earned badges get green styling
        color: 'bg-gradient-to-br from-green-500/20 to-green-600/30 border border-green-500/30'
      }));
  }, [userData?.badges]);

  const visibleBadges = showAllBadges ? achievedBadges : achievedBadges.slice(0, 6);

  const handleMouseEnter = (badgeId) => {
    setShowTooltip(prev => ({ ...prev, [badgeId]: true }));
  };

  const handleMouseLeave = (badgeId) => {
    setShowTooltip(prev => ({ ...prev, [badgeId]: false }));
  };

  if (achievedBadges.length === 0) {
    return (
      <div className="mt-4">
        <h2 className="text-xl font-semibold mb-4">Badges</h2>
        <div className="bg-dark-gray/20 border border-gray-700 rounded-lg p-6 text-center">
          <p className="text-gray-400">No badges earned yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Badges</h2>
        {achievedBadges.length > 6 && (
          <button
            onClick={() => setShowAllBadges(!showAllBadges)}
            className="text-sm text-blue-400 hover:text-blue-300 transition-colors duration-200"
          >
            {showAllBadges ? 'Show Less' : `Show All (${achievedBadges.length})`}
          </button>
        )}
      </div>

      {/* Badges Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {visibleBadges.map((badge, index) => (
          <motion.div
            key={badge.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            whileHover={{
              y: -5,
              scale: 1.03
            }}
            className={`${badge.color} backdrop-blur-sm rounded-lg p-4 flex flex-col items-center text-center transition-all duration-300 shadow-lg relative cursor-pointer`}
            onMouseEnter={() => handleMouseEnter(badge.id)}
            onMouseLeave={() => handleMouseLeave(badge.id)}
          >

            <h3 className="font-medium text-sm">{badge.name}</h3>
            <p className="text-xs text-gray-400 mt-1 line-clamp-2">{badge.description}</p>

            <div className="mt-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
                ✓ Earned
              </span>
            </div>

            {/* Enhanced Tooltip */}
            {showTooltip[badge.id] && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-20"
              >
                <div className="bg-gray-900/95 backdrop-blur-sm text-white text-sm rounded-lg px-3 py-2 w-64 border border-gray-600 shadow-2xl">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">{badge.name}</p>
                      <p className="text-gray-300 mt-1 text-sm leading-relaxed">{badge.description}</p>

                      <div className="mt-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 font-medium">
                          ✓ Badge Earned
                        </span>
                      </div>

                      {badge.earned_at && (
                        <div className="mt-3">
                          <p className="text-blue-400 text-sm">
                            Earned: {new Date(badge.earned_at).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default function OtherUserProfile() {
  const { user, getAuthenticatedApi } = useAuth();
  const { user_id } = useParams();
  const [activeTab, setActiveTab] = useState('Posts');
  const [showAllBadges, setShowAllBadges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const profileRef = useRef(null);

  // Memoize API instance to prevent recreation on every render
  const api = useMemo(() => {
    return getAuthenticatedApi?.();
  }, [getAuthenticatedApi]);

  // Memoize the fetch function to prevent recreation
  const fetchUserData = useCallback(async () => {
    if (!api || !user_id) return;

    setIsLoading(true);
    try {
      const response = await api.get(`/profile/user/${user_id}`);
      const backendProfile = response.data;
      const uid = backendProfile?.user_id || user_id;

      const [postsRes, commentsRes, interpretationsRes, blogsRes] = await Promise.all([
        api.get(`/post/user/${uid}`),
        api.get(`/comments/user/${uid}`),
        api.get(`/interpretations/user/${uid}`),
        api.get(`/blog/by-author/${uid}`)
      ]);

      backendProfile.info = backendProfile.info || {};
      backendProfile.info.activity = {
        posts: postsRes.data || [],
        comments: commentsRes.data || [],
        interpretations: interpretationsRes.data || [],
        blogs: blogsRes.data || []
      };

      const transformedData = transformProfileData(backendProfile, user);
      setUserData(transformedData);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUserData(null);
    } finally {
      setIsLoading(false);
    }
  }, [api, user, user_id]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Share profile handler
  const handleShare = async () => {
    const url = `${window.location.origin}/profile/user/${userData?.id}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Check out this profile on Graphology Forum`,
          text: `Explore handwriting insights and analysis by this user.`,
          url: url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        // You might want to add a toast notification here
        console.log('Profile link copied to clipboard!');
      }
    } catch (error) {
      try {
        await navigator.clipboard.writeText(url);
        console.log('Profile link copied to clipboard!');
      } catch (clipboardError) {
        console.error('Failed to share or copy link');
      }
    }
  };

  // XP Progress calculation
  const xpForCurrentLevel = 100;
  const progress = userData ? (userData.xp % xpForCurrentLevel) / xpForCurrentLevel * 100 : 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0B0C0D] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-[#0B0C0D] flex items-center justify-center">
        <div className="text-white text-center">
          <h2 className="text-2xl font-bold mb-4">User Not Found</h2>
          <p className="text-gray-400">The user you're looking for doesn't exist or has been removed.</p>
          <Link to="/forum" className="mt-4 inline-block bg-primary hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors">
            Back to Forum
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C0D] text-white pb-12">
      {/* Profile Header - Read-only for other users */}
      <ProfileHeader
        userData={userData}
        isOwnProfile={false}
        handleShare={handleShare}
        copySuccess={false}
        handleProfilePictureUpload={() => {}} // No-op for other users
        previewImage={null}
      />

      {/* Main Content */}
      <div ref={profileRef} className="max-w-6xl mx-auto px-6 pt-20 pb-8">
        {/* User Info - Read-only for other users */}
        <UserInfo
          userData={userData}
          isOwnProfile={false}
          editSections={{}}
          formData={{}}
          handleFormChange={() => {}} // No-op for other users
          progress={progress}
          xpForCurrentLevel={xpForCurrentLevel}
          BADGE_ICONS={BADGE_ICONS}
        />

        {/* Professional Profile - Read-only for other users */}
        <ProfessionalProfile
          userData={userData}
          isOwnProfile={false}
          editSections={{}}
          formData={{}}
          handleFormChange={() => {}} // No-op for other users
          handleArrayChange={() => {}} // No-op for other users
          addArrayItem={() => {}} // No-op for other users
          removeArrayItem={() => {}} // No-op for other users
          toggleEditMode={() => {}} // No-op for other users
          saveChanges={() => {}} // No-op for other users
          cancelEdit={() => {}} // No-op for other users
        />

        {/* Badges Section - Updated to match BadgesSection design */}
        <OtherUserBadgesSection
          userData={userData}
          showAllBadges={showAllBadges}
          setShowAllBadges={setShowAllBadges}
        />

        {/* Activity Section - Read-only for other users */}
        <ActivitySection
          userData={userData}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>
    </div>
  );
}